import React, { useEffect, useState } from "react";
import Datatable from "../../components/shared/datatable/Datatable";
import { toast } from "react-hot-toast";
import {
  useGetEmployeeAttendanceListQuery,
  useMarkEmployeeAttendanceMutation,
} from "./employeeAttendanceApi";
import useTranslator from "../../hooks/useTranslator";

const EmployeeAttendenceTab = ({ tab }) => {
  const { _t } = useTranslator();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filter, setFilter] = useState({});
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [currentRecordStatus, setCurrentRecordStatus] = useState(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  const queryArgs = {
    working_area_id: tab,
    page,
    pageSize,
    ...filter,
  };

  const {
    data: employeeAttendanceList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetEmployeeAttendanceListQuery(queryArgs, {
    skip: !shouldFetch,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      employee_role_id: "",
    }));
    setPage(1);
    setShouldFetch(true);
  }, [tab]);

  useEffect(() => {
    setShouldFetch(true);
  }, [filter]);

  useEffect(() => {
    if (employeeAttendanceList || isError) {
      setShouldFetch(false);
    }
  }, [employeeAttendanceList, isError]);

  const [markAttendance, { isLoading: isMarking }] =
    useMarkEmployeeAttendanceMutation();

  const columns = [
    { label: _t("name", "Name"), accessor: "name" },
    { label: _t("email", "Email"), accessor: "email" },
    { label: _t("date_of_hire", "Date of Hire"), accessor: "date_of_hire" },
    { label: _t("phone_number", "Phone Number"), accessor: "mobile" },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ value }) => (
        <span className={value === 1 ? "cmn_badge" : "cmn_badge inactive"}>
          {value === 1 ? _t("active", "Active") : _t("inactive", "Inactive")}
        </span>
      ),
    },
    {
      label: _t("role", "Role"),
      accessor: "role.name",
      captialize: true,
      translate: true,
    },
    {
      label: _t("mark_attendance", "Mark Attendance"),
      accessor: "actions",
      cell: ({ row }) => {
        const status = row?.attendance?.status;
        const isAbsent = status === "absent";
        const isPresent = status === "present";

        const presentClass = [
          "pe-2",
          isPresent
            ? "bg-success text-white px-2 rounded pointer-events-none"
            : "cursor-pointer",
        ].join(" ");

        const absentClass = [
          isAbsent
            ? "bg-danger text-white px-2 rounded pointer-events-none"
            : "cursor-pointer",
        ].join(" ");

        const presentText =
          isMarking &&
          currentRecordId == row.id &&
          currentRecordStatus == "present"
            ? _t("loading", "Loading...")
            : _t("present", "Present");

        const absentText =
          isMarking &&
          currentRecordId == row.id &&
          currentRecordStatus == "absent"
            ? _t("loading", "Loading...")
            : _t("absent", "Absent");

        return (
          <div className="d-flex gap-3">
            <span
              className={`${presentClass} cmn_grey`}
              onClick={() => {
                if ((!status || isAbsent) && !isMarking) {
                  markAttendanceHandler(row, "present");
                }
              }}
            >
              {presentText}
            </span>
            <span
              className={`${absentClass} cmn_grey`}
              onClick={() => {
                if ((!status || isPresent) && !isMarking) {
                  markAttendanceHandler(row, "absent");
                }
              }}
            >
              {absentText}
            </span>
          </div>
        );
      },
    },
  ];

  const markAttendanceHandler = async (user, status) => {
    try {
      if (user && user.id) {
        setCurrentRecordId(user.id);
        setCurrentRecordStatus(status);
        const payload = {
          user_id: user.id,
          status,
        };
        await markAttendance(payload).unwrap();
        setShouldFetch(true);
        // toast.success("Attendance marked successfully!");
      } else {
        toast.error(_t("error_while_marking_attendance", "Unable to mark attendance. Please try again."));
      }
    } catch (error) {
      toast.error(
        error?.data?.message || "Error deleting employee. Please try again."
      );
    } finally {
    }
  };

  const handlePageChange = (newPage) => {
    setShouldFetch(true);
    setPage(newPage);
  };

  return (
    <div>      
        <Datatable
          title={_t("attendance_log", "Attendance Log")}
          columns={columns}
          tableData={employeeAttendanceList?.data}
          pagination
          page={page}
          onPageChange={handlePageChange}
          showFilter={setFilter}
          showFilterButton={true}
          filterModule="staffAttendance"
          filters={filter}
          isLoading={isLoading}
        />      
    </div>
  );
};

export default EmployeeAttendenceTab;
